/*
 * photoStore.js — fixture photo blobs, in IndexedDB.
 *
 * Photos are the first thing Flow stores that is genuinely BINARY and
 * genuinely large. localStorage is the wrong home for them twice over: it
 * holds strings, so a blob has to be base64'd (a third larger again), and its
 * quota is a few megabytes shared with every other key — two site photos would
 * evict the workday facts sitting beside them. IndexedDB stores Blobs natively
 * and has room, so the binary lives here and the rest of the fixture stores
 * keep holding nothing but small JSON.
 *
 * BUILD 003, fixture-only: this is local to one browser, exactly like the other
 * fixture stores. It is not a photo service and adds no infrastructure or cost.
 * A real build replaces it behind the same small interface.
 */
const DB_NAME = 'flow.fixture.photos'
const STORE = 'photos'
const VERSION = 1

function open() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('NO_INDEXEDDB')); return }
    const req = indexedDB.open(DB_NAME, VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' })
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function tx(db, mode, fn) {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode)
    const store = t.objectStore(STORE)
    let out
    try { out = fn(store) } catch (e) { reject(e); return }
    t.oncomplete = () => resolve(out?.result ?? out)
    t.onerror = () => reject(t.error)
    t.onabort = () => reject(t.error)
  })
}

export function newPhotoId() {
  if (globalThis.crypto?.randomUUID) return `photo-${globalThis.crypto.randomUUID()}`
  return `photo-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`
}

/** Store one photo. Returns its id. */
export async function putPhoto({ id = newPhotoId(), name, type, blob }) {
  const db = await open()
  await tx(db, 'readwrite', (s) => s.put({ id, name: name ?? 'photo', type: type ?? blob?.type ?? '', blob, createdAt: new Date().toISOString() }))
  db.close()
  return id
}

/** One photo record, or null. */
export async function getPhoto(id) {
  const db = await open()
  const rec = await tx(db, 'readonly', (s) => s.get(id))
  db.close()
  return rec ?? null
}

/**
 * Several photos, in the order asked for. Missing ids are skipped rather than
 * throwing: a reference with no blob behind it should degrade to "one fewer
 * photo", never to a broken Assignment.
 */
export async function getPhotos(ids = []) {
  if (!ids.length) return []
  const db = await open()
  const out = []
  await new Promise((resolve, reject) => {
    const t = db.transaction(STORE, 'readonly')
    const store = t.objectStore(STORE)
    ids.forEach((id, i) => {
      const r = store.get(id)
      r.onsuccess = () => { if (r.result) out[i] = r.result }
    })
    t.oncomplete = resolve
    t.onerror = () => reject(t.error)
  })
  db.close()
  return out.filter(Boolean)
}

/*
 * Photo blobs are NOT deleted when an Assignment is voided. A voided
 * Assignment keeps its prior content and history (Build 003 requirement), and
 * its photos are part of that content. This exists for the one case where a
 * photo is removed from a draft before it was ever saved.
 */
export async function deletePhoto(id) {
  const db = await open()
  await tx(db, 'readwrite', (s) => s.delete(id))
  db.close()
}

/** Object URL for display. Caller revokes it. */
export function photoObjectURL(rec) {
  try { return rec?.blob ? URL.createObjectURL(rec.blob) : null } catch { return null }
}
